// LifeEventsWidget (TASK-576 Stage F / US-6) — life events promoted to a
// first-class canvas widget instead of a buried DnaCard row. Self-fetches the
// canonical KG store via useClientDna (same source DnaCard reads), maps the
// enriched life_event items (event_type/date/financial_impact/quote from Stage C)
// onto the existing LifeEventItem renderer — reuse, don't rebuild.
//
// US-10 (chips layer): client-side quick-chip facets over the already-fetched
// events — no new endpoint, no LLM. The natural-language filter bar is the
// incremental layer on top and is intentionally deferred (ponytail).
import { useMemo, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { WidgetContainer } from '../WidgetContainer';
import { useClientDna } from '../../../hooks/useClientDna';
import type { DnaItem } from '../../../services/knowledgeGraphService';
import LifeEventItem from '../../meeting/PostMeetingHub/LifeEventItem';
import type { LifeEvent } from '../../../types/meeting';

// Event types that carry a MANDATORY compliance action. Mirror of the backend
// map `core/life_event_actions.py::_ACTION_MAP` (the types whose first/primary
// action is a compliance task) — keep in sync. Powers the "compliance reviews
// due" facet + the on-tile badge.
const MANDATORY_TRIGGER_TYPES = new Set([
  'inheritance', 'business_sale', 'liquidity_event',
  'relocation', 'retirement', 'divorce', 'marriage', 'death',
]);

type ChipKey = 'all' | 'compliance' | 'high_impact';
const CHIPS: { key: ChipKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'compliance', label: 'Compliance reviews due' },
  { key: 'high_impact', label: 'High impact' },
];

function toLifeEvent(item: DnaItem): LifeEvent {
  return {
    id: item.event_id ?? item.entry_id,
    date: item.event_date ?? '',
    type: (item.event_type as LifeEvent['type']) ?? 'other',
    description: item.text,
    client_quote: item.quote,
    financialImpact: (item.financial_impact as LifeEvent['financialImpact']) ?? undefined,
  };
}

function matches(chip: ChipKey, e: LifeEvent): boolean {
  if (chip === 'compliance') return MANDATORY_TRIGGER_TYPES.has(e.type);
  if (chip === 'high_impact') return e.financialImpact === 'high';
  return true;
}

interface Props {
  clientId: string;
}

export const LifeEventsWidget = ({ clientId }: Props) => {
  const { data: dna, isLoading, isError } = useClientDna(clientId);
  const [chip, setChip] = useState<ChipKey>('all');

  const all = useMemo<LifeEvent[]>(
    () =>
      (dna?.life_events ?? [])
        .map(toLifeEvent)
        // most recent first; fuzzy/unparseable dates sink to the bottom
        .sort((a, b) => (new Date(b.date).getTime() || 0) - (new Date(a.date).getTime() || 0)),
    [dna],
  );
  const shown = useMemo(() => all.filter((e) => matches(chip, e)), [all, chip]);

  return (
    <WidgetContainer
      title="Life Events"
      source="Knowledge Graph"
      toolbar={
        <div className="flex items-center gap-1.5">
          {CHIPS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setChip(key)}
              className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${
                chip === key
                  ? 'bg-kielis-navy text-white border-kielis-navy'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {label}
            </button>
          ))}
          {chip !== 'all' && (
            <span className="text-xs text-slate-400">
              {shown.length} of {all.length}
            </span>
          )}
        </div>
      }
    >
      {isLoading ? (
        <div className="p-4 text-sm text-gray-500">Loading life events…</div>
      ) : isError ? (
        <div className="p-4 text-sm text-red-500">Could not load life events.</div>
      ) : shown.length === 0 ? (
        <div className="p-4 text-sm text-gray-500">
          {all.length === 0 ? 'No life events captured for this client.' : 'No events match this filter.'}
        </div>
      ) : (
        <div className="space-y-2 p-2">
          {shown.map((e) => (
            <div key={e.id} className="relative">
              {MANDATORY_TRIGGER_TYPES.has(e.type) && (
                <span
                  className="absolute right-2 top-2 z-10 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-amber-100 text-amber-800 border border-amber-200"
                  title="This event triggers a mandatory compliance review"
                >
                  <ShieldAlert className="w-3 h-3" /> Compliance
                </span>
              )}
              <LifeEventItem event={e} />
            </div>
          ))}
        </div>
      )}
    </WidgetContainer>
  );
};

export default LifeEventsWidget;
